import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';

interface ApplicantUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface ApplicationRow {
  id: number;
  applicationType: string;
  underwritingMode: string;
  /** True when the figure is a provisional starter amount, not an assessment. */
  provisional: boolean;
  user: ApplicantUser;
  /** The line the applicant already holds. Null when this is their first. */
  account: {
    creditLimit: number;
    usedCredit: number;
    availableCredit: number;
    status: string;
  } | null;
  amountRequested: number;
  purpose: string | null;
  recommendedLimit: number | null;
  capacityLimit: number | null;
  bandAtDecision: string | null;
  scoreAtDecision: number | null;
  decisionReasons: string[] | null;
  status:
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'RESUBMISSION_REQUESTED'
    | 'COUNTER_OFFERED'
    | 'COUNTER_DECLINED';
  approvedLimit: number | null;
  /** Cash actually paid out. Null on line/limit applications — nothing moves. */
  disbursedAmount: number | null;
  termPeriods: number | null;
  installmentFrequency: string | null;
  rejectionReason: string | null;
  resubmissionNotes: string | null;
  resubmittedAt: string | null;
  offeredLimit: number | null;
  offerReason: string | null;
  offerRespondedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

const STATUS_FILTERS = [
  'PENDING',
  'COUNTER_OFFERED',
  'RESUBMISSION_REQUESTED',
  'APPROVED',
  'REJECTED',
  'ALL',
] as const;

const statusColor: Record<string, string> = {
  PENDING: 'text-gold bg-gold/10',
  APPROVED: 'text-success bg-success/10',
  REJECTED: 'text-error bg-error/10',
  RESUBMISSION_REQUESTED: 'text-info bg-info/10',
  COUNTER_OFFERED: 'text-gold bg-gold/10',
  COUNTER_DECLINED: 'text-textTertiary bg-surface2',
};

const typeLabel: Record<string, string> = {
  NEW_LINE: 'New line',
  LIMIT_INCREASE: 'Limit increase',
  CASH_ADVANCE: 'Cash advance',
  PURCHASE_TOPUP: 'Purchase top-up',
};

/**
 * What the amount field should start at, which depends entirely on what the
 * field MEANS for this application type.
 *
 * On a cash advance the field is the sum being paid out, so it starts at what
 * the applicant asked for — pre-filling the engine's line recommendation there
 * is how a $50 request came to be recorded as "Approved: $300".
 *
 * On a line or limit application the field is the ceiling, so it starts at the
 * recommendation, floored at any line the applicant already holds: approving a
 * figure below that would cut the limit rather than raise it.
 */
const defaultApproval = (row: ApplicationRow): number => {
  if (row.applicationType === 'CASH_ADVANCE') return row.amountRequested;
  return Math.max(
    row.recommendedLimit ?? row.amountRequested,
    row.account?.creditLimit ?? 0,
  );
};

const money = (n: number | null) =>
  n === null || n === undefined ? '—' : `$${n.toFixed(2)}`;

export default function AdminCreditBuilderApplications() {
  const [rows, setRows] = useState<ApplicationRow[]>([]);
  const [filter, setFilter] =
    useState<(typeof STATUS_FILTERS)[number]>('PENDING');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<number | null>(null);
  const [approvedLimit, setApprovedLimit] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [termPeriods, setTermPeriods] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [resubmissionNotes, setResubmissionNotes] = useState('');
  const [offeredLimit, setOfferedLimit] = useState('');
  const [offerReason, setOfferReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback((status: (typeof STATUS_FILTERS)[number]) => {
    setLoading(true);
    api
      .get('/credit-builder/admin/applications', {
        params: status === 'ALL' ? {} : { status },
      })
      .then((res) => setRows(res.data.items ?? []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const startReview = (row: ApplicationRow) => {
    setActingId(actingId === row.id ? null : row.id);
    setApprovedLimit(String(defaultApproval(row)));
    setInterestRate('');
    setTermPeriods(row.termPeriods ? String(row.termPeriods) : '');
    setRejectionReason('');
    setResubmissionNotes('');
    setOfferedLimit('');
    setOfferReason('');
  };

  const review = async (
    id: number,
    status:
      | 'APPROVED'
      | 'REJECTED'
      | 'RESUBMISSION_REQUESTED'
      | 'COUNTER_OFFERED',
    row?: ApplicationRow,
  ) => {
    if (status === 'APPROVED' && (!approvedLimit || Number(approvedLimit) <= 0)) {
      alert('An approved limit is required');
      return;
    }
    // Mirrors the server-side guard so the reviewer learns this at the point of
    // the click rather than through a failed request.
    if (
      status === 'APPROVED' &&
      row?.applicationType === 'LIMIT_INCREASE' &&
      row.account &&
      Number(approvedLimit) < row.account.creditLimit
    ) {
      alert(
        `This is a limit increase, but ${money(Number(approvedLimit))} is below their current ${money(row.account.creditLimit)} line. Approve a higher figure, or reject the request.`,
      );
      return;
    }
    if (status === 'REJECTED' && !rejectionReason.trim()) {
      alert('A rejection reason is required');
      return;
    }
    if (status === 'RESUBMISSION_REQUESTED' && !resubmissionNotes.trim()) {
      alert('Explain what the applicant needs to provide');
      return;
    }
    if (status === 'COUNTER_OFFERED') {
      if (!offeredLimit || Number(offeredLimit) <= 0) {
        alert('Enter the amount you can offer');
        return;
      }
      if (row && Number(offeredLimit) >= row.amountRequested) {
        alert(
          'A counter-offer must be less than the amount requested — approve it instead',
        );
        return;
      }
      if (!offerReason.trim()) {
        alert('Explain why the offer is lower than the amount requested');
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.patch(`/credit-builder/admin/applications/${id}`, {
        status,
        approvedLimit:
          status === 'APPROVED' ? Number(approvedLimit) : undefined,
        interestRate:
          status === 'APPROVED' && interestRate
            ? Number(interestRate)
            : undefined,
        termPeriods:
          status === 'APPROVED' && termPeriods ? Number(termPeriods) : undefined,
        rejectionReason:
          status === 'REJECTED' ? rejectionReason.trim() : undefined,
        resubmissionNotes:
          status === 'RESUBMISSION_REQUESTED'
            ? resubmissionNotes.trim()
            : undefined,
        offeredLimit:
          status === 'COUNTER_OFFERED' ? Number(offeredLimit) : undefined,
        offerReason:
          status === 'COUNTER_OFFERED' ? offerReason.trim() : undefined,
      });
      setActingId(null);
      load(filter);
    } catch (e: any) {
      alert(
        e?.response?.data?.message ?? 'Could not review application',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const applicantName = (u: ApplicantUser) =>
    [u.firstName, u.lastName].filter(Boolean).join(' ') || `User #${u.id}`;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-textPrimary">
          Credit Builder Applications
        </h1>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-sm px-3 py-1.5 rounded-lg border transition ${
                filter === s
                  ? 'bg-gold/10 border-gold/40 text-gold'
                  : 'bg-surface1 border-border text-textSecondary hover:border-gold/30'
              }`}
            >
              {s === 'RESUBMISSION_REQUESTED' ? 'AWAITING INFO' : s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-surface1 rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-textTertiary">
            <tr>
              <th className="text-left py-3 px-4">Applicant</th>
              <th className="text-left py-3 px-4">Type</th>
              <th className="text-left py-3 px-4">Requested</th>
              <th className="text-left py-3 px-4">Current line</th>
              <th className="text-left py-3 px-4">Recommended</th>
              <th className="text-left py-3 px-4">Assessment</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-textTertiary">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-textTertiary">
                  No applications.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-b border-border/60 hover:bg-surface2/50 transition">
                    <td className="py-3 px-4 text-textPrimary">
                      {applicantName(r.user)}
                      <div className="text-textTertiary text-xs">
                        {r.user.email}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-textTertiary">
                      {typeLabel[r.applicationType] ?? r.applicationType}
                      <div className="text-textTertiary text-xs">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-textPrimary font-mono">
                      {money(r.amountRequested)}
                    </td>
                    {/* Without this the console recommended a figure against a
                        line it never showed, so a limit increase could be
                        adjudicated blind to the limit it was increasing. */}
                    <td className="py-3 px-4 font-mono">
                      {r.account ? (
                        <>
                          <span className="text-textPrimary">
                            {money(r.account.creditLimit)}
                          </span>
                          <div className="text-textTertiary text-xs font-sans">
                            {money(r.account.availableCredit)} available
                          </div>
                        </>
                      ) : (
                        <span className="text-textTertiary text-xs font-sans">
                          No line yet
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 font-mono">
                      <span
                        className={
                          r.provisional ? 'text-warning' : 'text-textPrimary'
                        }
                      >
                        {money(r.recommendedLimit)}
                      </span>
                      {r.provisional && (
                        <div className="text-warning text-xs font-sans">
                          provisional
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-textTertiary">
                      {/* A TEMP_NEW_USER row has no band or capacity by design.
                          Showing blanks here would read as missing data rather
                          than deliberately-skipped math. */}
                      {r.provisional ? (
                        <span className="text-warning text-xs">
                          Not assessed — new account
                        </span>
                      ) : (
                        <>
                          <div>
                            {r.bandAtDecision ?? '—'}
                            {r.scoreAtDecision
                              ? ` · score ${r.scoreAtDecision}`
                              : ''}
                          </div>
                          <div className="text-xs">
                            capacity {money(r.capacityLimit)}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${statusColor[r.status]}`}
                      >
                        {r.status === 'RESUBMISSION_REQUESTED'
                          ? 'AWAITING INFO'
                          : r.status === 'COUNTER_OFFERED'
                            ? 'OFFER SENT'
                            : r.status === 'COUNTER_DECLINED'
                              ? 'OFFER DECLINED'
                              : r.status}
                      </span>
                      {r.resubmittedAt && (
                        <div className="text-textTertiary text-xs mt-1">
                          resubmitted
                        </div>
                      )}
                      {r.offeredLimit !== null && (
                        <div className="text-textTertiary text-xs mt-1">
                          offered {money(r.offeredLimit)}
                          {r.status === 'COUNTER_OFFERED' &&
                            ' · awaiting reply'}
                        </div>
                      )}
                      {r.disbursedAmount !== null && (
                        <div className="text-textTertiary text-xs mt-1">
                          paid out {money(r.disbursedAmount)}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {r.status === 'PENDING' && (
                        <button
                          onClick={() => startReview(r)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition"
                        >
                          Review
                        </button>
                      )}
                    </td>
                  </tr>

                  {actingId === r.id && (
                    <tr className="border-b border-border/60 bg-surface2/30">
                      <td colSpan={8} className="py-4 px-4">
                        {/* Spells out what the number in the field will do, so
                            the reviewer is never guessing whether they are
                            setting a ceiling or paying out cash. */}
                        <div className="mb-4 text-textSecondary text-sm">
                          {r.applicationType === 'CASH_ADVANCE' ? (
                            <>
                              Paying out cash. The line stays at{' '}
                              <strong className="text-textPrimary">
                                {money(r.account?.creditLimit ?? 0)}
                              </strong>{' '}
                              unless the advance needs more room.
                            </>
                          ) : r.account ? (
                            <>
                              Setting a new ceiling. Currently{' '}
                              <strong className="text-textPrimary">
                                {money(r.account.creditLimit)}
                              </strong>{' '}
                              with {money(r.account.usedCredit)} drawn — a
                              figure below that will be refused.
                            </>
                          ) : (
                            <>
                              Setting a new ceiling. This applicant holds no
                              line yet, so no money moves on approval.
                            </>
                          )}
                        </div>

                        {r.provisional && (
                          <div className="mb-4 px-4 py-3 rounded-lg bg-warning/10 border border-warning/30 text-warning text-sm">
                            <strong className="font-semibold">
                              Provisional — decide this one yourself.
                            </strong>{' '}
                            This applicant has no usable history, so no capacity
                            assessment was run. The{' '}
                            {money(r.recommendedLimit)} shown is a placeholder
                            starter figure pending the approved new-user
                            underwriting model, not a recommendation.
                          </div>
                        )}

                        {r.decisionReasons && r.decisionReasons.length > 0 && (
                          <div className="mb-4">
                            <div className="text-textTertiary text-xs mb-1">
                              Underwriting notes
                            </div>
                            <ul className="text-textSecondary text-xs space-y-0.5">
                              {r.decisionReasons.map((reason, i) => (
                                <li key={i}>· {reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {r.purpose && (
                          <div className="mb-4 text-textSecondary text-sm">
                            <span className="text-textTertiary text-xs">
                              Purpose:{' '}
                            </span>
                            {r.purpose}
                          </div>
                        )}

                        <div className="flex gap-3 items-end flex-wrap">
                          <label className="flex flex-col gap-1">
                            <span className="text-textTertiary text-xs">
                              {/* For a cash advance this number is the sum
                                  advanced, not a new ceiling — approving $18
                                  does not cut an existing $500 line to $18. */}
                              {r.applicationType === 'CASH_ADVANCE'
                                ? 'Advance amount ($)'
                                : 'Approved limit ($)'}
                            </span>
                            <input
                              type="number"
                              value={approvedLimit}
                              onChange={(e) => setApprovedLimit(e.target.value)}
                              className="w-32 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
                            />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-textTertiary text-xs">
                              Interest rate (%)
                            </span>
                            <input
                              type="number"
                              value={interestRate}
                              onChange={(e) => setInterestRate(e.target.value)}
                              placeholder="0"
                              className="w-28 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
                            />
                          </label>
                          {r.applicationType === 'CASH_ADVANCE' && (
                            <label className="flex flex-col gap-1">
                              <span className="text-textTertiary text-xs">
                                Payments
                              </span>
                              <input
                                type="number"
                                value={termPeriods}
                                onChange={(e) => setTermPeriods(e.target.value)}
                                placeholder="6"
                                className="w-24 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
                              />
                            </label>
                          )}
                        </div>

                        <div className="flex gap-3 items-end flex-wrap mt-3">
                          <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Rejection reason (required only if rejecting)"
                            rows={2}
                            className="flex-1 min-w-[200px] px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                          <textarea
                            value={resubmissionNotes}
                            onChange={(e) =>
                              setResubmissionNotes(e.target.value)
                            }
                            placeholder="What do you need from them? (required only if requesting more info)"
                            rows={2}
                            className="flex-1 min-w-[200px] px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                        </div>

                        {/* Counter-offer: extend less than was asked for and
                            let the applicant decide, rather than approving a
                            smaller number over their head or rejecting them
                            outright. Nothing is provisioned until they accept. */}
                        <div className="flex gap-3 items-end flex-wrap mt-3 pt-3 border-t border-border">
                          <label className="flex flex-col gap-1">
                            <span className="text-textTertiary text-xs">
                              {r.applicationType === 'CASH_ADVANCE'
                                ? 'Advance instead ($)'
                                : 'Offer instead ($)'}
                            </span>
                            <input
                              type="number"
                              value={offeredLimit}
                              onChange={(e) => setOfferedLimit(e.target.value)}
                              placeholder="100"
                              className="w-32 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
                            />
                          </label>
                          <textarea
                            value={offerReason}
                            onChange={(e) => setOfferReason(e.target.value)}
                            placeholder="Why this is lower than they asked for — the applicant sees this"
                            rows={2}
                            className="flex-1 min-w-[240px] px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                          <button
                            onClick={() => review(r.id, 'COUNTER_OFFERED', r)}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-gold/10 border border-gold/30 text-gold font-medium hover:bg-gold/20 transition disabled:opacity-40"
                          >
                            Send offer
                          </button>
                        </div>

                        <div className="flex gap-3 mt-3">
                          <button
                            onClick={() => review(r.id, 'APPROVED', r)}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-success/10 border border-success/30 text-success font-medium hover:bg-success/20 transition disabled:opacity-40"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() =>
                              review(r.id, 'RESUBMISSION_REQUESTED')
                            }
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-info/10 border border-info/30 text-info font-medium hover:bg-info/20 transition disabled:opacity-40"
                          >
                            Request more info
                          </button>
                          <button
                            onClick={() => review(r.id, 'REJECTED')}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-error/10 border border-error/30 text-error font-medium hover:bg-error/20 transition disabled:opacity-40"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
